"use client";

import { FC, useState } from "react";
import { Content, isFilled } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import clsx from "clsx";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { motion } from "framer-motion";
import { PrismicNextImage } from "@prismicio/next";
import { MotionPrismicNextLink } from "@/components/ui/motion-prismic-next-link";
import { Button } from "@/components/ui/button";
import arrowRight from "@/assets/common/arrow-right.svg";
import Image from "next/image";
import noise from "@/assets/noise.png";
import studentLiftFeatureCardShape from "@/assets/studentLiftFeatureCardShape.svg";

/**
 * Props for `StudentLifeFeatureSection`.
 */
export type StudentLifeFeatureSectionProps =
  SliceComponentProps<Content.StudentLifeFeatureSectionSlice>;

/**
 * Component for "StudentLifeFeatureSection" Slices.
 */
const StudentLifeFeatureSection: FC<StudentLifeFeatureSectionProps> = ({
  slice,
}) => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={clsx(
        "py-20",
        slice.variation === "right" ? "bg-white" : "bg-[#F8FCFF]"
      )}
    >
      <Bounded className="container mx-auto">
        <div className="flex flex-col xl:flex-row xl:items-center items-start justify-center gap-8">
          <div
            className={clsx(
              "space-y-6 flex-1",
              slice.variation === "right" ? "order-2" : "order-1"
            )}
          >
            <motion.div
              className="font-archivo font-semibold text-5xl leading-16 text-[#033D61] mb-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <PrismicRichText
                field={slice.primary.title}
                components={{
                  label: ({ node, children }) => {
                    if (node.data.label === "Highlight") {
                      return <span className="text-secondary">{children}</span>;
                    }
                    return <span className="text-primary">{children}</span>;
                  },
                }}
              />
            </motion.div>
            <motion.div
              className="font-merriweather font-normal text-xl leading-8 text-left text-[#033D61] mb-8 max-w-[565px]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <PrismicRichText field={slice.primary.description} />
            </motion.div>
          </div>
          <div
            className={clsx(
              "grid grid-cols-1 md:grid-cols-2 gap-4 flex-1",
              slice.variation === "right" ? "order-1" : "order-2"
            )}
          >
            {slice.primary.features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="h-full"
                onHoverStart={() => {
                  setHoveredCard(index);
                }}
                onHoverEnd={() => {
                  setHoveredCard(null);
                }}
              >
                <motion.div
                  className="relative h-full transform-3d perspective-[1000px]"
                  animate={{
                    rotateY: hoveredCard === index ? 180 : 0,
                  }}
                  transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
                >
                  <div
                    className={clsx(
                      "backface-hidden px-[33px] py-9 text-[#033D61] rounded-xl overflow-hidden h-full",
                      slice.variation === "right" ? "bg-[#FAFDFF]" : "bg-[#FFF]"
                    )}
                  >
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-[42px] bg-[#EBF7FF]">
                      <PrismicNextImage
                        field={feature.icon}
                        width={24}
                        height={24}
                      />
                    </div>
                    <div className="font-merriweather text-xl font-bold leading-8 mb-3.5">
                      {feature.title}
                    </div>
                    <div className="font-archivo text-base font-normal leading-6">
                      {feature.description}
                    </div>
                  </div>
                  <div className="absolute inset-0 backface-hidden rotate-y-180 rounded-xl overflow-hidden">
                    <div className="relative w-full h-full px-[33px] py-9 bg-[#045B91] text-[#FAFDFF]">
                      <div
                        className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.3]"
                        style={{
                          background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
                        }}
                      />
                      <Image
                        src={studentLiftFeatureCardShape.src}
                        alt="shape"
                        width={473}
                        height={439}
                        className="absolute h-[120%] w-full top-[-60px] -left-0.5 pointer-events-none select-none"
                      />
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mb-[42px] bg-[#D9F7F21A]">
                        <PrismicNextImage
                          field={feature.reverted_icon}
                          width={24}
                          height={24}
                        />
                      </div>
                      <div className="font-merriweather relative text-xl font-bold leading-8 mb-3.5">
                        {feature.title}
                      </div>
                      <div className="font-archivo text-base relative font-normal leading-6">
                        {feature.description}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
        {isFilled.link(slice.primary.call_to_action_button_link) && (
          <div className="mt-8 w-full flex items-center justify-center">
            <MotionPrismicNextLink
              field={slice.primary.call_to_action_button_link}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <Button className="px-5 py-3 h-auto! cursor-pointer font-archivo gap-2.5 text-base font-medium leading-6 rounded-[40px] bg-[linear-gradient(264deg,#0798F2_-29%,#43D9BD_85.45%)] hover:bg-[linear-gradient(264deg,#43D9BD_-29%,#0798F2_85.45%)] text-[#033D61] transition-all duration-75 hover:[&_img]:-rotate-45">
                {slice.primary.call_to_action_button_link.text}
                <div className="bg-[#EBF7FF] w-[30px] h-[30px] rounded-full flex items-center justify-center">
                  <Image
                    src={arrowRight.src}
                    alt="arrow right"
                    width={16}
                    height={16}
                  />
                </div>
              </Button>
            </MotionPrismicNextLink>
          </div>
        )}
      </Bounded>
    </section>
  );
};

export default StudentLifeFeatureSection;
